import { ActionTree } from 'vuex';
import { AllModuleState, RootState } from '@/store';
import { GroupInfoExtension, State } from './state';
import groupApi from '_c/business/api/group';
import imApi from '_c/business/api/im';
import userApi from '_c/business/api/user';
import topicApi from '_c/business/api/topic';
import { MessageManager } from '_c/business/manager/message';
import { ChanCateBaseInfo, GroupUserInfo, Platform } from '_c/typings/proto';
import { TConversation } from '_c/typings/model/im';
import { DBManager } from '_c/business/storage';

type StrList=Array<string>;

export enum EJoinGroupFrom {
  invited,
  preview,
}

const actions: ActionTree<State, RootState> = {
  async getGroupList({ state, commit, dispatch }, { page = 1, pageSize = 100 }): Promise<void> {
    return Promise.all([
      groupApi.getGroupListPos(),
      groupApi.getGroupList({
        type: 1,
        page,
        pageSize,
      }),
      groupApi.getGroupList({
        type: 2,
        page,
        pageSize,
      }),
    ])
      .then(([res1, res2, res3]) => res2.infoList.concat(res3.infoList)
        .sort((a, b) => {
          const index1 = res1.gids.indexOf(a.info.gid);
          const index2 = res1.gids.indexOf(b.info.gid);
          // eslint-disable-next-line no-param-reassign
          a.isJoined = true;
          // eslint-disable-next-line no-param-reassign
          b.isJoined = true;
          if (index1 >= 0 && index2 >= 0) {
            return index1 - index2;
          }
          return parseInt(b.joinedTime, 10) - parseInt(a.joinedTime, 10);
        })
        .filter(item => item.info.status !== 2))
      .then((groupList) => {
        // 注意：这里的群组信息因为后台原因，只能拉取基本信息，和用户态相关信息，需要主动调用 getGroupInfo 获取
        commit('setGroupList', groupList);
        commit('setChannelList', groupList);
        // commit('setSelectGroupId', groupList[0].info.gid);
        let channelIdList: Array<string> = [];
        Object.keys(state.channelList).forEach((groupId) => {
          channelIdList = channelIdList.concat(state.channelList[groupId].map(channel => channel.cid));
        });
        const gidList = groupList.map(item => item.info.gid);
        // 触发推广位列表更新
        dispatch('getPromotionList', [...gidList]);
        // 触发中心化入口更新
        dispatch('getCenterEntranceList', [...gidList]);
        // 触发 at 更新
        dispatch('getAtCount', channelIdList);
        // 触发拉取最新消息
        dispatch('getLastMessage', channelIdList);
        // 生成折叠数据并初始化设置
        const cateMap: Record<string, Array<ChanCateBaseInfo>> = {};
        groupList.forEach((item) => {
          cateMap[item.info.gid] = [...item.info.cateList];
        });
        commit('resetChannelFoldData', cateMap);
        // 触发群组信息更新
        // dispatch('getGroupInfo', { groupId: state.selectGroupId });
      });
  },
  // 更新中心化推广位信息
  getPromotionList({ commit }, gidList: StrList): void {
    let len = gidList.length;
    while (len > 0) {
      const tempGidList = gidList.splice(0, 20);
      groupApi.getGroupPromotionInfo(tempGidList, Platform.PlatformPCWeb)
        .then((res) => {
          commit('setPromotionList', res.conf);
        });
      len = len - 20;
    }
  },
  // 更新中心化入口信息
  getCenterEntranceList({ commit }, gidList: StrList): void {
    let len = gidList.length;
    while (len > 0) {
      const tempGidList = gidList.splice(0, 20);
      groupApi.getGroupCenterEntranceInfo(tempGidList, Platform.PlatformPCWeb)
        .then((res) => {
          commit('setCenterEntranceList', res.conf);
        });
      len = len - 20;
    }
  },
  async getAtCount({ commit, getters }, channelIdList: Array<string>): Promise<void> {
    // 获取和我相关的 at 信息
    return imApi.userAtInfoApart(channelIdList).then((res) => {
      Object.keys(res.channelToAtnum).forEach((channelId) => {
        const groupId = getters.channelToGroupMap[channelId];
        const atCount = parseInt(res.channelToAtnum[channelId] || '0', 10);
        const atAllCount = parseInt(res.channelToAtAllNum[channelId] || '0', 10);
        commit('setAtCount', { groupId, channelId, count: atCount });
        // 除去 at all 的，剩下的全部都是 at 和我相关滴
        commit('setAtMeCount', { groupId, channelId, count: atCount - atAllCount });
      });
    });
  },
  async getLastMessage({ state, commit }, channelIdList?: Array<string>): Promise<void> {
    // 先从DB里面拉取 lastMessage 显示出来，再去 TIM 中获取真实的消息
    if (channelIdList) {
      DBManager.getInstance().getLastMessage(channelIdList)
        .then((messageMap) => {
          if (messageMap) {
            Object.keys(messageMap).forEach((channelId) => {
              const message = messageMap[channelId];
              const { groupId } = message.channelMsg;
              // 如果没有 lastMessage，那么就设入 DB 的 lastMessage
              if (!(state.lastMessageList?.[groupId]?.[channelId])) {
                commit('setLastMessage', { groupId, channelId, message });
              }
            });
          }
        });
    }
    // 通过回调形式，这样就不用等所有的回来才进行设置了
    MessageManager.getInstance().getConversationList(channelIdList, false, (conversation: TConversation) => {
      if (conversation?.lastMessage?.channelMsg) {
        const { groupId, channelId } = conversation.lastMessage.channelMsg;
        const unreadCount = conversation.unreadCount || 0;
        commit('setLastMessage', { groupId, channelId, message: conversation.lastMessage });
        commit('setUnreadCount', { groupId, channelId, count: unreadCount });
      }
    });
  },
  async getGroupInfo({ commit, state, dispatch, rootState }, { groupId = '', isForce = false }): Promise<void> {
    const group = state.groupList.find(group => group.info.gid === groupId);
    if (!group || (!isForce && group?.isUpdate)) return;
    const uid = (rootState as AllModuleState).user.baseInfo?.uid || '';
    const isFirstJoin = state.firstJoinMap[groupId];
    return Promise.all([
      groupApi.getGroupInfo({
        gid: groupId,
        needChannel: true,
        needMute: true,
        needOwnerInfo: true,
        needGrpNick: true,
        needWelInfoReportData: isFirstJoin,
      }),
      dispatch('getGroupUserInfo', { groupId, uids: [uid] }), // 更新群组信息的同时，更新当前用户在此群组的信息
    ]).then(([res]) => {
      // 首次加入时存储欢迎信息
      if (isFirstJoin) {
        commit('setWelcomeGroupInfo', {
          gid: groupId,
          groupInfo: res.info.info,
        });
        commit('setABTestReportData', {
          gid: groupId,
          reportInfo: res.welInfoReportInfo,
        });
      }
      commit('updateGroupInfo', res.info);
      commit('setChannelList', [res.info]);
      commit('setChannelFoldData', { [res.info.info.gid]: [...res.info.info.cateList] });
    });
  },
  async getGroupUserInfo({ commit, rootState }, { groupId = '', uids = [] }): Promise<Record<string, GroupUserInfo> | undefined> {
    return userApi.getGrpUserInfoBatch(groupId, uids).then((res) => {
      if (res?.ret === 0) {
        const uid = (rootState as AllModuleState).user.baseInfo?.uid || '';
        if (res?.mInfo[uid]) {
          commit('setGroupCurrentUserInfo', { groupId, info: res?.mInfo[uid] });
        }
        return res?.mInfo;
      }
    });
  },
  /** 添加预览群组 */
  addPreviewGroup({ commit, state, dispatch }, groupInfo: GroupInfoExtension): void {
    const previewGroupInfo: GroupInfoExtension = {
      ...groupInfo,
      isPreview: true,
    };
    // 只能有一个预览群组
    const groupList = state.groupList.filter(item => !item.isPreview);
    commit('setGroupList', [
      previewGroupInfo,
      ...groupList,
    ]);
    commit('setChannelList', [
      previewGroupInfo,
      ...groupList,
    ]);
    const cidList = groupInfo.chanList.map(item => item.cid);
    dispatch('getLastMessage', cidList);
  },
  /** 移除预览群组 */
  removePreviewGroup({ commit, state }) {
    const groupList = state.groupList.filter(item => !item.isPreview);
    commit('setGroupList', groupList);
    commit('setChannelList', groupList);
  },
  /** 加入群组 */
  joinGroup({ dispatch, state, commit }, { gid, from }: { gid: string, from: EJoinGroupFrom}) {
    return groupApi.joinGroup(gid).then(() => {
      // 设置首次加入群组为true 后续展示欢迎弹窗
      commit('setFirstJoin', {
        gid,
        isFirstJoin: true,
      });
      if (from === EJoinGroupFrom.invited) {
        return;
      } if (from === EJoinGroupFrom.preview) {
        // 如果群组已经在预览状态（groupList中有数据）
        // 触发推广位列表更新
        dispatch('getPromotionList', [gid]);
        // 触发中心化入口更新
        dispatch('getCenterEntranceList', [gid]);
        const findGroup = state.groupList.find(item => item.info.gid === gid);
        if (findGroup) {
          findGroup.isPreview = false;
        }
        // 这里由于加群后需要展示加群弹窗，所以必须触发更新，原有逻辑判断遗弃
        dispatch('getGroupInfo', { groupId: gid, isForce: true });
      }
    });
  },
  /** 退出群组 */
  async quitGroup({ commit, state }, { gid }: {gid: string}) {
    let selectGroupId = '';
    let selectChannelId = '';
    await groupApi.quitGroup(gid);
    const index = state.groupList.findIndex(group => group.info.gid === gid);
    if (index >= 0) {
      const group = state.groupList[index];
      if (group.info.gid === state.selectGroupId && state.groupList.length > 1) {
        const nextGroup = index === state.groupList.length - 1 ? state.groupList[0] : state.groupList[index + 1];
        if (nextGroup) {
          selectGroupId = nextGroup.info.gid;
          const defCid = nextGroup?.info.defCid;
          const defaultChannel = nextGroup.chanList.find(channel => channel.cid === defCid);
          const checkDefault = defaultChannel?.type !== 1 && defaultChannel?.isCmdChan === 0;
          if (checkDefault) {
            selectChannelId = defCid;
          } else {
            const nextChannel = nextGroup.chanList.find(channel => channel.type !== 1 && channel.isCmdChan === 0);
            selectChannelId = nextChannel?.cid ?? '';
          }
        }
      }
    }
    commit('quitGroup', gid);
    commit('unsetChannelFoldData', [gid]);
    return {
      selectGroupId,
      selectChannelId,
    };
  },
  getGroupTopicList({ commit }, groupId: string) {
    topicApi.getGroupTopicListSimple({
      gid: groupId,
      page: 1,
      pageSize: 20,
    }).then((data) => {
      data.topicList.forEach((item) => {
        if (!item.isExpire) {
          commit('setTopicList', { groupId, channelId: item.channelId, topicInfo: item.topicInfo });
        }
      });
    });
  },
};

export default actions;
